import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { cn } from '#client/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant | undefined;
  /** 32 / 40 / 48px — the same three heights as the table density steps. */
  size?: ButtonSize | undefined;
  loading?: boolean | undefined;
  fullWidth?: boolean | undefined;
  icon?: ReactNode | undefined;
  children?: ReactNode | undefined;
  /**
   * Layout utilities from the calling page. Appearance belongs to
   * client/styles/components/button.css.
   *
   * These reliably win: component CSS sits in `@layer components` and
   * utilities in the later `utilities` layer, so layer order rather than
   * selector specificity decides.
   */
  className?: string | undefined;
  /**
   * React 19 passes `ref` as an ordinary prop, so no `forwardRef` wrapper is
   * needed — it just has to be declared. Used where focus must be moved to a
   * button programmatically, as on the result-entry screen.
   */
  ref?: Ref<HTMLButtonElement> | undefined;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  fullWidth = false,
  icon,
  children,
  className,
  disabled,
  type = 'button',
  ref,
  ...rest
}: ButtonProps) {
  // An icon with no label is a square target and must carry its own
  // accessible name; callers pass `aria-label`.
  const iconOnly = Boolean(icon) && !children;

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'c-button',
        `c-button--${variant}`,
        `c-button--${size}`,
        iconOnly && 'c-button--icon-only',
        fullWidth && 'c-button--full',
        className,
      )}
    >
      {loading && <span className="c-button__spinner" aria-hidden="true" />}
      {icon && !loading && <span className="c-button__icon">{icon}</span>}
      {children && <span>{children}</span>}
    </button>
  );
}
